import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { FormControlLabel, Switch, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";

const formatObjectField = value => {
  if (value == null) {
    return "";
  }

  if (typeof value === "object" && Object.keys(value).length === 0) {
    return "";
  }

  return JSON.stringify(value, null, 2);
};

const BotpressProductsDialog = props => {
  const {
    open,
    productRecord,
    onClose,
    onAccept,
    onDelete,
    dialogTitle = "Edit Product",
    okMessage = "Ok",
    cancelMessage = "Cancel",
    maxWidth = "md",
    fullWidth = true,
    deleteMessage = "Delete",
  } = props;

  const theme = useTheme();

  const [editedProductRecord] = useState({ ...productRecord });

  const [name, setName] = useState(productRecord.name || "");
  const [description, setDescription] = useState(productRecord.description || "");
  const [price, setPrice] = useState(productRecord.price || "");
  const [computed, setComputed] = useState(formatObjectField(productRecord.computed));
  const [state, setState] = useState(productRecord.state || "");
  const [type, setType] = useState(productRecord.type || "");
  const [archived, setArchived] = useState(productRecord.archived || false);
  const [title, setTitle] = useState(productRecord.title || "");
  const [summary, setSummary] = useState(productRecord.summary || "");
  const [startDate, setStartDate] = useState(productRecord.startDate || "");
  const [endDate, setEndDate] = useState(productRecord.endDate || "");
  const [cta, setCta] = useState(formatObjectField(productRecord.cta));
  const [assets, setAssets] = useState(formatObjectField(productRecord.assets));
  const [metadata, setMetadata] = useState(formatObjectField(productRecord.metadata));
  const [matchSignals, setMatchSignals] = useState(productRecord.matchSignals || "");
  const [softRequirements, setSoftRequirements] = useState(productRecord.softRequirements || "");
  const [showConfirm, setShowConfirm] = useState(false);

  const onDeleteHandler = () => {
    onDelete(editedProductRecord);
  };

  const onAcceptHandler = () => {
    try {
      editedProductRecord.name = name;
      editedProductRecord.description = description;
      editedProductRecord.price = price;
      editedProductRecord.state = state;
      editedProductRecord.type = type;
      editedProductRecord.archived = archived;
      editedProductRecord.title = title;
      editedProductRecord.summary = summary;
      editedProductRecord.startDate = startDate;
      editedProductRecord.endDate = endDate;
      editedProductRecord.matchSignals = matchSignals;
      editedProductRecord.softRequirements = softRequirements;

      try {
        editedProductRecord.computed = computed.trim() ? JSON.parse(computed) : {};
      } catch {
        editedProductRecord.computed = {};
      }

      try {
        editedProductRecord.cta = cta.trim() ? JSON.parse(cta) : {};
      } catch {
        editedProductRecord.cta = {};
      }

      try {
        editedProductRecord.assets = assets.trim() ? JSON.parse(assets) : {};
      } catch {
        editedProductRecord.assets = {};
      }

      try {
        editedProductRecord.metadata = metadata.trim() ? JSON.parse(metadata) : {};
      } catch {
        editedProductRecord.metadata = {};
      }

      onAccept(editedProductRecord);
    } catch (error) {
      console.log(error);
    }
  };

  const onCloseHandler = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onCloseHandler();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>

      <DialogContent>
        <Grid spacing={1} container size={{ xs: 12 }}>
          {productRecord.id != null && (
            <Grid item size={{ xs: 12 }}>
              <Typography variant="body2" color="textSecondary">
                Product ID: {productRecord.id}
              </Typography>
            </Grid>
          )}

          <Grid item size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Product Name"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productName"}
              value={name}
              onChange={event => setName(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Description"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productDescription"}
              rowCount={5}
              multiline={true}
              value={description}
              onChange={event => setDescription(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Price"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productPrice"}
              value={price}
              onChange={event => setPrice(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Title"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productTitle"}
              value={title}
              onChange={event => setTitle(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Summary"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productSummary"}
              rowCount={4}
              multiline={true}
              value={summary}
              onChange={event => setSummary(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"State"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productState"}
              value={state}
              onChange={event => setState(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Type"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productType"}
              value={type}
              onChange={event => setType(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Start Date"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productStartDate"}
              value={startDate}
              onChange={event => setStartDate(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 6 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"End Date"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productEndDate"}
              value={endDate}
              onChange={event => setEndDate(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={archived}
                  onChange={event => setArchived(event.target.checked)}
                  color="secondary"
                />
              }
              label={"Archived"}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Computed"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productComputed"}
              rowCount={4}
              multiline={true}
              value={computed}
              onChange={event => setComputed(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"CTA"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productCta"}
              rowCount={4}
              multiline={true}
              value={cta}
              onChange={event => setCta(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Assets"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productAssets"}
              rowCount={4}
              multiline={true}
              value={assets}
              onChange={event => setAssets(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Metadata"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productMetadata"}
              rowCount={4}
              multiline={true}
              value={metadata}
              onChange={event => setMetadata(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Match Signals"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productMatchSignals"}
              rowCount={3}
              multiline={true}
              value={matchSignals}
              onChange={event => setMatchSignals(event.target.value)}
            />
          </Grid>

          <Grid item container size={{ xs: 12 }}>
            <OmniTextField
              sx={{ width: "100%" }}
              label={"Soft Requirements"}
              type="textInput"
              variant="outlined"
              name={uuid()}
              id={"productSoftRequirements"}
              rowCount={3}
              multiline={true}
              value={softRequirements}
              onChange={event => setSoftRequirements(event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box
          sx={{
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          {productRecord.id != null && props.onDelete != null && (
            <OmniResponsiveButton
              icon={<DeleteIcon />}
              onClick={() => setShowConfirm(true)}
              buttonText={deleteMessage}
              tooltipTitle=""
            />
          )}
        </Box>

        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          {cancelMessage && (
            <Button
              onClick={onCloseHandler}
              color="secondary"
              variant="contained"
              sx={{ marginRight: theme.spacing(2) }}
            >
              {cancelMessage}
            </Button>
          )}

          <Button onClick={onAcceptHandler} color="secondary" variant="contained">
            {okMessage}
          </Button>
        </Box>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this Product?"}
          onAccept={() => {
            setShowConfirm(false);
            onDeleteHandler();
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default BotpressProductsDialog;
